import { AfterViewInit, Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { ForecastGraphDataClean } from '../../interfaces';
import Plotly from 'plotly.js-dist-min';
import { DatePipe } from '@angular/common';
import { CHART_RELEVANT_PLOTLY_CONFIG } from '../plotly-chart-config';

@Component({
  selector: 'app-line-chart',
  standalone: false,
  templateUrl: './line-chart.component.html',
  styleUrl: './line-chart.component.scss',
  providers: [DatePipe]
})
export class LineChartComponent implements AfterViewInit, OnChanges {
  @Input() data!: ForecastGraphDataClean;

  @ViewChild('chart') chart!: ElementRef;
  private plotlyInstance: any;
  private readonly plotlyConfig: Partial<Plotly.Config> = CHART_RELEVANT_PLOTLY_CONFIG as Partial<Plotly.Config>;

  graph: any = {
    data: [],
    layout: {
      shapes: [],
      xaxis: {
        title: 'Feature Value',
        zeroline: false,
        showline: true,
        linewidth: 0.5,
        linecolor: '#333',
      },
      yaxis: {
        title: {
          text: 'Price (USD)',
        },
        showline: false,
        zeroline: false,
      },
      margin: { l: 50, r: 50, b: 50, t: 50 },
      height: 500,
      width: 1100,
      plot_bgcolor: 'rgba(0,0,0,0)',
      showlegend: true,
      legend: {
        orientation: 'h',
        yanchor: 'bottom',
        y: 1.02,
        xanchor: 'right',
        x: 1,
      },
      hovermode: 'x unified',
    },
  };

  constructor(private datePipe: DatePipe){}

  ngAfterViewInit() {
    this.plotGraph(this.data);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.plotGraph(changes['data'].currentValue);
    }
  }

  plotGraph(data: ForecastGraphDataClean) {
    if (!this.chart || !this.chart.nativeElement) {
      console.warn('Chart element not ready yet, skipping plot.');
      return;
    }

    const plotData = [];

    if (data.forecastHigh && data.forecastLow) {
      plotData.push({
        y: data.forecastHigh,
        mode: 'lines',
        name: 'Forecast High',
        line: { color: 'rgba(0, 0, 0, 0)' },
        showlegend: false,
        hoverinfo: 'skip',
      });

      let formattedData = [];
      for (let i = 0; i <= data.forecastLow.length; i++) {
        if (data.forecastLow[i] && data.forecastHigh[i]) {
          let low = data.forecastLow[i]!.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            style: 'currency',
            currency: 'USD',
          });
          let high = data.forecastHigh[i]!.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            style: 'currency',
            currency: 'USD',
          });
          formattedData.push(low + ' - ' + high);
        } else {
          formattedData.push(null);
        }
      }

      plotData.push({
        y: data.forecastLow,
        mode: 'lines',
        name: 'Forecast Range',
        line: { color: 'rgba(0, 0, 0, 0)' },
        fill: 'tonexty',
        fillcolor: 'rgba(85, 190, 185, 0.2)',
        showlegend: true,
        hoverinfo: 'y',
        hovertemplate: '%{customdata}',
        customdata: formattedData,
      });
    }

    if (data.emForecast) {
      let parsedAverage = data.emForecast.map((elem) =>
        elem
          ? elem.toLocaleString('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
              style: 'currency',
              currency: 'USD',
            })
          : null
      );
      plotData.push({
        y: data.emForecast,
        mode: 'lines',
        name: 'EM Forecast',
        line: { color: '#6929c4' },
        hoverinfo: 'y',
        hovertemplate: '%{customdata}',
        customdata: parsedAverage,
      });
    }

    if (data.argusForecast) {
      let parsedForecast = data.argusForecast.map((elem) =>
        elem
          ? elem.toLocaleString('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
              style: 'currency',
              currency: 'USD',
            })
          : null
      );
      plotData.push({
        y: data.argusForecast,
        mode: 'lines',
        name: 'Argus Forecast',
        line: { color: '#ff7f0e' },
        hoverinfo: 'y',
        hovertemplate: '%{customdata}',
        customdata: parsedForecast,
      });
    }

    if (data.actual) {
      plotData.push({
        y: data.actual,
        mode: 'lines+markers',
        name: 'Actual',
        line: { color: '#2cacde' },
        marker: { size: 6 },
      });
    }

    let baseline = [];
    let lastActual = null;
    if (data.argusForecast && data.actual) {
      for (let i = 0; i < data.argusForecast?.length - 1; i++) {
        if (lastActual === null && data.argusForecast[i + 1]) {
          lastActual = data.actual[i];
        }
        baseline.push(lastActual);
      }
      baseline.push(lastActual);
    }

    plotData.push({
      y: baseline,
      mode: 'lines',
      line: {
        color: '#2cacde',
        dash: 'dot',
      },
      marker: { size: 6 },
      showlegend: false,
      hoverinfo: 'none',
    });

    const layout = JSON.parse(JSON.stringify(this.graph.layout));

    layout.xaxis.tickvals = null;
    layout.xaxis.ticktext = null;

    if (data.yearMonth) {
      let res = data.yearMonth.map((elem) => this.convertYyyyMmToMonthName(elem ?? ''));
      // console.log(res, data.yearMonth);
      layout.xaxis.tickvals = Array.from({ length: data.yearMonth.length }, (_, i) => i);
      layout.xaxis.ticktext = data.yearMonth.map((elem) => this.convertYyyyMmToMonthName(elem ?? ''));
    }

    let current = 0;
    for(let i=0; i < this.data.actual.length - 1; i++){
      if(this.data.actual[i + 1] === null){
        current = i + .5;
        break;
      }
    }
    layout.shapes.push({
      type: 'line',
      x0: current,
      x1: current,
      y0: 0,
      y1: 1,
      xref: 'x',
      yref: 'paper',
      line: {
        color: 'gray',
        width: 2,
        dash: 'dash',
      },
    });
    if (this.plotlyInstance) {
      Plotly.react(this.chart.nativeElement, plotData, layout, this.plotlyConfig);
    } else {
      Plotly.newPlot(this.chart.nativeElement, plotData, layout, this.plotlyConfig).then(
        (instance) => {
          this.plotlyInstance = instance;
        }
      );
    }
  }
  convertYyyyMmToMonthName(yyyyMmString: string): string | null {
    const date = new Date(`${yyyyMmString}-15`); 
  
    if (isNaN(date.getTime())) {
      return null;
    }
    return this.datePipe.transform(date, 'MMM'); 
  }
}
